import { Injectable } from '@angular/core';

export interface GSCRow {
  query?: string;
  page?: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  date?: string;
  isEstimated?: boolean; // TODO: REMOVE WHEN GSC API IS IMPLEMENTED - CSV estimation flag only
  [key: string]: any;
}

export interface SummaryStats {
  totalClicks: number;
  totalImpressions: number;
  avgCtr: number;
  avgPosition: number;
}

export interface DateSeries {
  date: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {

  normalizeGSCData(data: any[], sourceType: 'summary' | 'page' | 'query' | 'countries' | 'devices' | 'search_appearance' | 'dates' = 'page'): GSCRow[] {
    if (!data || data.length === 0) return [];

    return data.map(row => {
      const normalizedRow: GSCRow = {
        clicks: 0,
        impressions: 0,
        ctr: 0,
        position: 0
      };

      // Extract numeric values using all possible column variations
      normalizedRow.clicks = this.extractNumericValue(row, ['clicks', 'click']);
      normalizedRow.impressions = this.extractNumericValue(row, ['impressions', 'impression']);
      normalizedRow.ctr = this.extractNumericValue(row, ['ctr', 'click_through_rate'], true); // Handle percentage
      normalizedRow.position = this.extractNumericValue(row, ['position', 'avg_position', 'average_position']);

      // Handle different data types
      if (sourceType === 'summary' || sourceType === 'dates') {
        // Dates.csv - extract date information
        normalizedRow.date = this.extractStringValue(row, ['date', 'day']) || 'Unknown';
        normalizedRow.page = 'Daily Summary'; // Label for display
      } else if (sourceType === 'page') {
        // Pages.csv - extract page URLs
        normalizedRow.page = this.extractStringValue(row, ['top_pages', 'page', 'url', 'pages']) || 'Unknown';
      } else if (sourceType === 'query') {
        // Queries.csv - extract search queries
        normalizedRow.query = this.extractStringValue(row, ['query', 'queries', 'search_query', 'keyword', 'top_queries']) || 'Unknown';
      } else if (sourceType === 'countries') {
        // Countries.csv
        normalizedRow.page = this.extractStringValue(row, ['country', 'countries']) || 'Unknown';
      } else if (sourceType === 'devices') {
        // Devices.csv
        normalizedRow.page = this.extractStringValue(row, ['device', 'devices']) || 'Unknown';
      } else if (sourceType === 'search_appearance') {
        // Search appearance.csv
        normalizedRow.page = this.extractStringValue(row, ['search_appearance', 'appearance']) || 'Unknown';
      }

      // Add additional fields for other dataset types (for getCellValue method)
      (normalizedRow as any).country = this.extractStringValue(row, ['country', 'countries']);
      (normalizedRow as any).device = this.extractStringValue(row, ['device', 'devices']);
      (normalizedRow as any).search_appearance = this.extractStringValue(row, ['search_appearance', 'appearance']);

      return normalizedRow;
    });
  }

  private extractNumericValue(row: any, possibleKeys: string[], isPercentage: boolean = false): number {
    for (const key of possibleKeys) {
      const value = row[key];
      if (value !== undefined && value !== null && value !== '') {
        return this.safeNumber(value, isPercentage);
      }
    }
    return 0;
  }

  private extractStringValue(row: any, possibleKeys: string[]): string | undefined {
    for (const key of possibleKeys) {
      const value = row[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return undefined;
  }

  calculateSummaryStats(data: GSCRow[]): SummaryStats {
    if (!data || data.length === 0) {
      return { totalClicks: 0, totalImpressions: 0, avgCtr: 0, avgPosition: 0 };
    }

    const totalClicks = data.reduce((sum, row) => {
      const clicks = this.safeNumber(row.clicks);
      return sum + clicks;
    }, 0);
    
    const totalImpressions = data.reduce((sum, row) => {
      const impressions = this.safeNumber(row.impressions);
      return sum + impressions;
    }, 0);
    
    // Calculate averages using GSC's methodology
    let avgCtr = 0;
    let avgPosition = 0;

    // For CTR: Use the overall CTR from total clicks/impressions (like GSC does)
    if (totalImpressions > 0) {
      avgCtr = (totalClicks / totalImpressions) * 100; // Convert to percentage
    }

    // For Position: Try to match GSC's methodology more closely
    // GSC uses "highest position" (best position) for each query
    // Since our CSV data already contains aggregated positions per query,
    // we need to be more careful about how we average them
    let weightedPositionSum = 0;
    let totalValidImpressions = 0;
    let simplePositionSum = 0;
    let validPositionCount = 0;

    data.forEach(row => {
      const impressions = this.safeNumber(row.impressions);
      const position = this.safeNumber(row.position);
      
      if (impressions > 0 && position > 0) {
        // Method 1: Impression-weighted (what we were doing)
        weightedPositionSum += position * impressions;
        totalValidImpressions += impressions;
        
        // Method 2: Simple average of query positions (closer to GSC's "highest position" approach)
        simplePositionSum += position;
        validPositionCount++;
      }
    });

    if (totalValidImpressions > 0 && validPositionCount > 0) {
      const impressionWeightedAvg = weightedPositionSum / totalValidImpressions;
      const simpleAvg = simplePositionSum / validPositionCount;
      
      // GSC seems to use a hybrid approach that's closer to simple averaging
      // but still considers impression volume for very high-impression queries
      // Let's try a weighted blend that favors simple average but considers impression weight
      const blendFactor = 0.3; // 30% impression-weighted, 70% simple average
      avgPosition = (impressionWeightedAvg * blendFactor) + (simpleAvg * (1 - blendFactor));
      
    } else if (validPositionCount > 0) {
      // Fallback: simple average
      avgPosition = simplePositionSum / validPositionCount;
    }

    return {
      totalClicks: this.safeNumber(totalClicks),
      totalImpressions: this.safeNumber(totalImpressions),
      avgCtr: this.safeNumber(avgCtr),
      avgPosition: this.safeNumber(avgPosition)
    };
  }

  private safeNumber(value: any, isPercentage: boolean = false): number {
    if (value === null || value === undefined || value === '') return 0;
    
    // Handle string values
    if (typeof value === 'string') {
      // Remove commas and whitespace
      let cleanValue = value.replace(/,/g, '').trim();
      
      // Handle percentage strings
      if (cleanValue.endsWith('%')) {
        const percentValue = cleanValue.slice(0, -1);
        const num = Number(percentValue);
        if (isNaN(num) || !isFinite(num)) return 0;
        // For CTR, we want to keep it as percentage (1.61% = 1.61, not 0.0161)
        // This matches how GSC stores CTR internally
        return isPercentage ? num : num / 100;
      }
      
      // Handle regular number strings
      const num = Number(cleanValue);
      if (isNaN(num) || !isFinite(num)) return 0;
      return num;
    }
    
    // Handle numeric values
    const num = Number(value);
    if (isNaN(num) || !isFinite(num)) return 0;
    return num;
  }

  groupDataByDate(data: GSCRow[]): DateSeries[] {
    if (!data || data.length === 0) return [];

    // If there's no date information, create a single aggregate
    const hasDateInfo = data.some(row => row.date && row.date !== 'Unknown');
    
    if (!hasDateInfo) {
      const stats = this.calculateSummaryStats(data);
      return [{
        date: 'All Data',
        clicks: stats.totalClicks,
        impressions: stats.totalImpressions,
        ctr: stats.avgCtr,
        position: stats.avgPosition
      }];
    }

    const dateGroups: { [date: string]: DateSeries } = {};

    data.forEach(row => {
      const date = row.date || 'Unknown';
      
      if (!dateGroups[date]) {
        dateGroups[date] = {
          date,
          clicks: 0,
          impressions: 0,
          ctr: 0,
          position: 0
        };
      }

      dateGroups[date].clicks += this.safeNumber(row.clicks);
      dateGroups[date].impressions += this.safeNumber(row.impressions);
    });

    // Calculate averages for each date
    Object.values(dateGroups).forEach(group => {
      const dayData = data.filter(row => (row.date || 'Unknown') === group.date);
      const stats = this.calculateSummaryStats(dayData);
      group.ctr = stats.avgCtr;
      group.position = stats.avgPosition;
    });

    const sortedGroups = Object.values(dateGroups);
    
    // Only sort by date if we have valid dates
    if (hasDateInfo) {
      return sortedGroups.sort((a, b) => {
        if (a.date === 'Unknown') return 1;
        if (b.date === 'Unknown') return -1;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });
    }

    return sortedGroups;
  }

  sortData(data: GSCRow[], sortBy: string, sortDirection: 'asc' | 'desc' = 'desc'): GSCRow[] {
    if (!data || data.length === 0) return [];

    return [...data].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      const aNum = Number(aVal) || 0;
      const bNum = Number(bVal) || 0;
      
      return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
    });
  }

  filterData(data: GSCRow[], searchTerm: string): GSCRow[] {
    if (!data || !searchTerm) return data;

    const term = searchTerm.toLowerCase();
    return data.filter(row => {
      return Object.values(row).some(value => 
        String(value).toLowerCase().includes(term)
      );
    });
  }

  formatNumber(num: number): string {
    const safeNum = this.safeNumber(num);
    // Return exact number with comma separators (like GSC)
    return safeNum.toLocaleString();
  }

  formatNumberCompact(num: number, isImpressions: boolean = false): string {
    const safeNum = this.safeNumber(num);
    const decimals = 2; // Use 2 decimals for both clicks and impressions to match GSC
    
    if (safeNum >= 1000000) {
      return (safeNum / 1000000).toFixed(decimals) + 'M';
    } else if (safeNum >= 1000) {
      return (safeNum / 1000).toFixed(decimals) + 'K';
    }
    return safeNum.toString();
  }

  formatPercentage(num: number): string {
    const safeNum = this.safeNumber(num);
    return safeNum.toFixed(1) + '%'; // CTR is already in percentage format
  }

  formatPosition(num: number): string {
    const safeNum = this.safeNumber(num);
    return safeNum.toFixed(1);
  }

  // Method to aggregate data that might be broken down by dimensions
  aggregateData(data: GSCRow[], aggregateBy: 'query' | 'page'): GSCRow[] {
    if (!data || data.length === 0) return [];

    const aggregated = new Map<string, GSCRow>();

    data.forEach(row => {
      const key = aggregateBy === 'query' ? (row.query || 'N/A') : (row.page || 'N/A');
      
      if (aggregated.has(key)) {
        const existing = aggregated.get(key)!;
        // Sum up the metrics
        existing.clicks += this.safeNumber(row.clicks);
        existing.impressions += this.safeNumber(row.impressions);
        // Keep the first non-null query/page value
        if (!existing.query && row.query) existing.query = row.query;
        if (!existing.page && row.page) existing.page = row.page;
      } else {
        aggregated.set(key, {
          query: row.query,
          page: row.page,
          clicks: this.safeNumber(row.clicks),
          impressions: this.safeNumber(row.impressions),
          ctr: 0, // Will be calculated after aggregation
          position: 0, // Will be calculated after aggregation
          date: row.date
        });
      }
    });

    // Calculate CTR and position for aggregated data
    return Array.from(aggregated.values()).map(row => {
      if (row.impressions > 0) {
        row.ctr = (row.clicks / row.impressions) * 100; // Convert to percentage
      }
      
      // For position, we need to do a weighted average based on original data
      const relatedRows = data.filter(r => 
        (aggregateBy === 'query' ? r.query === row.query : r.page === row.page)
      );
      
      let weightedPositionSum = 0;
      let totalImpressions = 0;
      
      relatedRows.forEach(r => {
        const impressions = this.safeNumber(r.impressions);
        const position = this.safeNumber(r.position);
        if (impressions > 0 && position > 0) {
          weightedPositionSum += position * impressions;
          totalImpressions += impressions;
        }
      });
      
      if (totalImpressions > 0) {
        row.position = weightedPositionSum / totalImpressions;
      }
      
      return row;
    });
  }

  private calculateMedianPosition(data: GSCRow[]): number {
    const positions = data
      .map(row => this.safeNumber(row.position))
      .filter(pos => pos > 0)
      .sort((a, b) => a - b);
    
    if (positions.length === 0) return 0;
    
    const mid = Math.floor(positions.length / 2);
    return positions.length % 2 === 0 
      ? (positions[mid - 1] + positions[mid]) / 2 
      : positions[mid];
  }

  private getPositionDistribution(data: GSCRow[]): { [range: string]: number } {
    const distribution: { [range: string]: number } = {
      '1-3': 0,
      '4-10': 0,
      '11-20': 0,
      '21-50': 0,
      '51+': 0
    };

    data.forEach(row => {
      const pos = this.safeNumber(row.position);
      if (pos <= 0) return;
      
      if (pos <= 3) distribution['1-3']++;
      else if (pos <= 10) distribution['4-10']++;
      else if (pos <= 20) distribution['11-20']++;
      else if (pos <= 50) distribution['21-50']++;
      else distribution['51+']++;
    });

    return distribution;
  }

  // TODO: REMOVE WHEN GSC API IS IMPLEMENTED
  // This entire estimation system is temporary for CSV import limitations only.
  // Once GSC API provides real granular data, this method should be deleted.
  // Method to estimate page/query performance for specific date ranges (CSV imports only)
  estimateFilteredData(
    itemData: GSCRow[], 
    dateSeriesData: DateSeries[], 
    startDate: string, 
    endDate: string,
    dataType: 'page' | 'query'
  ): GSCRow[] {
    if (!itemData || !dateSeriesData || dateSeriesData.length === 0) {
      return itemData;
    }

    // Filter date series for the target range
    const filteredDates = dateSeriesData.filter(d => 
      d.date >= startDate && d.date <= endDate
    );

    if (filteredDates.length === 0) {
      return itemData;
    }

    // Calculate total clicks in filtered period vs total period
    const filteredPeriodClicks = filteredDates.reduce((sum, d) => sum + d.clicks, 0);
    const totalPeriodClicks = itemData.reduce((sum, row) => sum + row.clicks, 0);
    
    if (totalPeriodClicks === 0 || filteredPeriodClicks === 0) {
      return itemData;
    }

    // Basic proportional ratio
    const baseProportion = filteredPeriodClicks / totalPeriodClicks;

    return itemData.map(row => {
      // Calculate performance weight (better performing pages get slight boost)
      const performanceWeight = this.calculatePerformanceWeight(row, itemData);
      
      // Apply weighted proportion
      const adjustedProportion = baseProportion * performanceWeight;
      
      return {
        ...row,
        clicks: Math.round(row.clicks * adjustedProportion),
        impressions: Math.round(row.impressions * adjustedProportion),
        // CTR and position remain the same (they're ratios/averages)
        ctr: row.ctr,
        position: row.position,
        // Add estimation flag for UI display
        isEstimated: true
      } as GSCRow & { isEstimated: boolean };
    });
  }

  // TODO: REMOVE WHEN GSC API IS IMPLEMENTED
  // Supporting method for CSV estimation system - delete with estimateFilteredData()
  private calculatePerformanceWeight(row: GSCRow, allData: GSCRow[]): number {
    // Calculate percentiles for CTR and position
    const allCtrs = allData.map(r => r.ctr).filter(c => c > 0).sort((a, b) => b - a);
    const allPositions = allData.map(r => r.position).filter(p => p > 0).sort((a, b) => a - b);
    
    if (allCtrs.length === 0 || allPositions.length === 0) {
      return 1.0; // No adjustment if no comparison data
    }

    // CTR weight (higher CTR = better performance)
    const ctrPercentile = allCtrs.indexOf(row.ctr) / allCtrs.length;
    const ctrWeight = 0.8 + (ctrPercentile * 0.4); // 0.8 to 1.2 range

    // Position weight (lower position = better performance)
    const positionPercentile = 1 - (allPositions.indexOf(row.position) / allPositions.length);
    const positionWeight = 0.8 + (positionPercentile * 0.4); // 0.8 to 1.2 range

    // Combined weight (slight adjustment, not dramatic)
    const combinedWeight = (ctrWeight + positionWeight) / 2;
    
    // Keep adjustments conservative (0.85 to 1.15 range)
    return Math.max(0.85, Math.min(1.15, combinedWeight));
  }
} 